import ReactDOM from "react-dom";
import React from "react";
import smoothscroll from 'smoothscroll-polyfill';
import 'lib/fastclick';

import {AppRouter} from 'new-components/router/Router';
import {createStore, combineReducers, applyMiddleware, compose} from "redux";
import createSagaMiddleware from 'redux-saga';
import {rootSaga} from "sagas/root.saga";
import {createMiddleware} from "redux-api-middleware";
import Config from './config';

import {actionLoggerMiddleware} from 'new-middlewares/action-logger.middleware';

import {start} from "new-actions/app-state.actions";

import {appStateReducer} from "reducers/app-state.reducer.ts";
import {restaurantReducer} from "reducers/restaurant.reducer.ts";
import {customerReducer} from "reducers/customer.reducer.ts";
import {localizationReducer} from "reducers/localization.reducer.ts";
import {restaurantListReducer} from "reducers/restaurant-list.reducer.ts";
import {authReducer} from "reducers/auth.reducer.ts";
import {menuReducer} from "reducers/menu.reducer.ts";
import {serverConfigReducer} from "reducers/server-config.reducer.ts";
import {orderLineReducer} from "reducers/order-line.reducer.ts";
import {orderingModeReducer} from "reducers/ordering-mode.reducer.ts";
import {orderTimeReducer} from "reducers/order-time.reducer.ts";
import {purchaseReducer} from 'reducers/purchase.reducer.ts';
import {orderReducer} from 'reducers/order.reducer.ts';
import {STORAGE_KEY} from "new-constants/common.constants";
import {fetchProgressReducer} from "reducers/fetch-progress.reducer.ts";
import {fetchErrorReducer} from "reducers/fetch-error.reducer.ts";
import {popupReducer} from "reducers/popup.reducer.ts";
import {paymentReducer} from "reducers/payment.reducer.ts";
import {cashpointsReducer} from "reducers/cashpoints.reducer.ts";
import {serverValuesReducer} from "reducers/server-values.reducer.ts";
import {deliveryAddressReducer} from "reducers/delivery-address.reducer.ts";
import {promocodeReducer} from "reducers/promocode.reducer.ts";
import {tableNumberReducer} from "reducers/table-number.reducer.ts";
import {pusherConnectionUpdaterMiddleware} from "new-middlewares/pusher-connection-updater.middleware";
import {connectivityReducer} from "reducers/connectivity.reducer.ts";
import {setOffline, setOnline} from "new-actions/connectivity.actions";
import {fetchMessageReducer} from "reducers/fetch-message.reducer.ts";
import {purchaseTokenReducer} from "reducers/purchase-token.reducer.ts";
import {
	initErrorLogging,
	setGlobalJsError,
	setJsError,
	subscribeErrorLogging
} from "managers/client-log.manager";
import {permissionReducer} from "reducers/permission.reducer.ts";
import {locationReducer} from "reducers/location.reducer.ts";
import {adyenReturnReducer} from 'reducers/adyen-return.reducer.ts'
import {getMigratedAuth} from "helpers/storage-migration.helper";
import Platform from "./platform";
import {apiErrorReplacerMiddleware} from "new-middlewares/api-error-replacer.middleware";
import {initPWAInstall, registerPWAServiceWorker} from "./helpers/pwa-helper";
import {sessionTimeReducer} from "./reducers/session-time.reducer";
import {pwaInstallAvailable, pwaInstalled} from "./actions/pwa.actions";
import {pwaPromptReducer} from "./reducers/pwa-prompt.reducer";
import {loadingReducer} from "./reducers/loading.reducer";
import {rewriteOldParamsToPath} from "./helpers/startup-params.helper";
import {getCurrentAppIdSelector, getCurrentCustomerSelector, getAdyenReturnModeSelector} from "./selectors";
import {routerReducer} from "react-router-redux";
import {composeWithDevTools} from 'redux-devtools-extension/developmentOnly';
import {getLoggingReduxEnhancer} from "./managers/client-log.manager";
import {MaintenancePage} from "./components/pages/maintenance/MaintenancePage";
import {getBrowserFullLocale, getBrowserShortLocaleAdaptedForBackend} from "./helpers/localization.helper";
import {initTracking} from "./helpers/tracking.helpers";

rewriteOldParamsToPath();
initErrorLogging();
initTracking();

if (Platform.isNativeApp) {
	document.addEventListener("deviceready", () => {
		if (navigator.splashscreen) {
			navigator.splashscreen.hide();
		}

		if(cordova?.InAppBrowser?.open){
			window.open = cordova.InAppBrowser.open;
		}
	});
}

const sagaMiddleware = createSagaMiddleware({
	onError(err) {
		setGlobalJsError(err);
		console.error(err);
	},
})
const apiMiddleware = createMiddleware();

let persistedState = localStorage && JSON.parse(localStorage.getItem(STORAGE_KEY + Config.BaseServerUrl));

if (!persistedState || typeof (persistedState) !== 'object') {
	persistedState = {
		state: {
			auth: getMigratedAuth()
		}
	};
}

if (persistedState.appVersion &&
	persistedState.appVersion !== Config.getAppVersion()
) {
	persistedState = {
		state: {
			auth: getMigratedAuth()
		}
	};
}

const middlewareEnhancer = applyMiddleware(
	apiMiddleware,
	apiErrorReplacerMiddleware,
	sagaMiddleware,
	actionLoggerMiddleware,
	pusherConnectionUpdaterMiddleware
);
const enhancers = [middlewareEnhancer, getLoggingReduxEnhancer()]
const composedEnhancers = composeWithDevTools(...enhancers);

const persistBlacklist = [
	'orderingMode',
	'fetchProgress',
	'fetchError',
	'fetchMessage',
	'popup',
	'purchase',
	'serverValues',
	'appState',
	'loading',
	'routing',
	'orderTime',
	'payment'
];

function getBlacklistedState(state) {
	const blacklistedState = {};
	Object.keys(state).forEach(key => blacklistedState[key] = persistBlacklist.includes(key) ? undefined : state[key]);

	return blacklistedState;
}

const store = createStore(combineReducers({
		appState: appStateReducer,
		connectivity: connectivityReducer,
		auth: authReducer,
		restaurant: restaurantReducer,
		customer: customerReducer,
		localization: localizationReducer,
		restaurantList: restaurantListReducer,
		menu: menuReducer,
		serverConfig: serverConfigReducer,
		orderLine: orderLineReducer,
		orderingMode: orderingModeReducer,
		orderTime: orderTimeReducer,
		purchase: purchaseReducer,
		purchaseToken: purchaseTokenReducer,
		order: orderReducer,
		fetchProgress: fetchProgressReducer,
		fetchError: fetchErrorReducer,
		fetchMessage: fetchMessageReducer,
		popup: popupReducer,
		payment: paymentReducer,
		cashpoints: cashpointsReducer,
		serverValues: serverValuesReducer,
		deliveryAddress: deliveryAddressReducer,
		promocode: promocodeReducer,
		tableNumber: tableNumberReducer,
		permission: permissionReducer,
		location: locationReducer,
		sessionTime: sessionTimeReducer,
		pwaPrompt: pwaPromptReducer,
		loading: loadingReducer,
		routing: routerReducer,
		adyenReturn: adyenReturnReducer
	}),
	getBlacklistedState(persistedState.state || {}),
	composedEnhancers,
);

let unsubscribePersistReduxState = null;

function persistReduxState(){
	try{
		localStorage && localStorage.setItem(STORAGE_KEY + Config.BaseServerUrl, JSON.stringify({
			appVersion: Config.getAppVersion(),
			state: getBlacklistedState(store.getState())
		}));
	}catch(e){
		if(e?.name === 'QuotaExceededError'){
			if(typeof unsubscribePersistReduxState === 'function'){
				unsubscribePersistReduxState();
			}
			localStorage.clear();
			setJsError('QuotaExceededError. LocalStorage cleared');
		}
	}
}
unsubscribePersistReduxState = store.subscribe(persistReduxState);

subscribeErrorLogging(store);
window.__store = store;

if (Config.prodMode) {
	window.__consoleLogSave = console.log;
	window.__consoleGroupSave = console.group;
	window.__consoleGroupEndSave = console.groupEnd;
	console.log = function () {
	};
	console.group = function () {
	};
	console.groupEnd = function () {
	};
}


function launchApp(){
	
	// if(Platform.isPageInBackground()) return;

	sagaMiddleware.run(rootSaga);
	try {
		ReactDOM.render(<AppRouter store={store}/>, document.getElementById('app'), () => console.log('Router rendered'));
	} catch (e) {
		setJsError('Router is not rendered. ' + e?.message, e);
		// ErrorHandlerHelper.cleanCacheAndReloadApp();
	}


	setTimeout(function initApp() {
		store.dispatch(
			start(
				getCurrentAppIdSelector(store.getState()),
				getAdyenReturnModeSelector(store.getState()),
				getCurrentCustomerSelector(store.getState())
			)
		);

		if (!Platform.isNativeApp) {
			registerPWAServiceWorker();
			initPWAInstall(
				() => store.dispatch(pwaInstallAvailable()),
				() => store.dispatch(pwaInstalled())
			);
		}

		smoothscroll.polyfill();
		FastClick.attach(document.body);

		window.addEventListener('online', () => store.dispatch(setOnline()));
		window.addEventListener('offline', () => store.dispatch(setOffline()));
	}, 0);
}

fetch(Config.operatingStatusUrl)
	.then(response => response.json())
	.then(data => {
		if(data.status === 'OFFLINE'){
			const locale = getBrowserShortLocaleAdaptedForBackend(getBrowserFullLocale()).replace('nb', 'no');
			const messageText = data.message[locale] || data.message['en'];

			ReactDOM.render(
				<MaintenancePage
					errorMessage={messageText}
					store={store}
				/>,
				document.getElementById('app')
			);
		}else{
			launchApp();
		}
	})
	.catch(error => {
		launchApp();
	});



